<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import ContactModel from '@models/Contact';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.contacts'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'last_name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'last_name', label: this.$t('table_labels.last_name'), sortable: true },
        { key: 'first_name', label: this.$t('table_labels.first_name'), sortable: true },
        { key: 'email', label: this.$t('table_labels.email'), sortable: true },
        { key: 'mobile_phone', label: this.$t('table_labels.mobile_phone'), sortable: true },
        { key: 'phone', label: this.$t('table_labels.phone'), sortable: true },
        { key: 'company_name', label: this.$t('table_labels.company'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    itemsProvider(ctx) {
      return ContactModel.orderBy(this.sortDir + ctx.sortBy)
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.contact') }))) {
        this.$wait.start('contact processing');
        const contact = new ContactModel({ id });
        await contact.delete();
        this.$wait.end('contact processing');
        this.$refs.asyncTable.refresh();
      }
    },
    updateFilter: debounce(function(value) {
      this.filters.search = value;
    }, 500),
    goToEditContact(contactId) {
      const routeData = this.$router.resolve({ name: 'contacts.edit', params: { id: contactId } });
      window.open(routeData.href, '_blank');
    },
    downloadVCard(id, firstName, lastName) {
      this.$wait.start('global loading');
      this.$http
        .get(`admin/contacts/${id}/vcard`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end('global loading');
          const blob = new Blob([response.data], { type: 'text/x-vcard' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `${lastName} ${firstName}.vcf`;
          link.click();
        })
        .catch(() => this.$wait.end('global loading'));
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.contacts')" :button-text="$t('titles.create_contact')" @button-pressed="$router.push({ name: 'contacts.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name'), $t('search.entities.company')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="contacts-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('contacts processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
       <!-- custom sorting icons -->
       <template #head()="{ label, field: { key, sortable } }">
              {{ label }}
              <template v-if="sortable">
                <!-- Custom icons -->
                <template>
                  <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
                  <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
                  <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
                </template>
              </template>
        </template>
        <template v-slot:cell(company_name)="row">
          <router-link v-if="row.item.company" :to="{ name: 'companies.edit', params: { id: row.item.company.id } }">{{ row.item.company.name }}</router-link>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="downloadVCard(row.item.id, row.item.first_name, row.item.last_name)">
              <FontAwesomeIcon :icon="['fas', 'address-card']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="goToEditContact(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
